import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/bits";
import api, { errMsg } from "@/lib/api";

export function PerformanceTab({ goals, employees, onRefresh }) {
  const [goalModal, setGoalModal] = useState(false);
  const [goal, setGoal] = useState({ employee_id: "", title: "", kpi: "", due_date: "" });
  const [reviewFor, setReviewFor] = useState(null);
  const [review, setReview] = useState({ rating: 5, feedback: "", recommendation: "none" });

  const createGoal = async () => {
    try {
      await api.post("/employer/goals", goal);
      toast.success("Goal assigned");
      setGoalModal(false);
      setGoal({ employee_id: "", title: "", kpi: "", due_date: "" });
      onRefresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const submitReview = async () => {
    try {
      await api.post(`/employer/goals/${reviewFor.id}/review`, { ...review, rating: Number(review.rating) });
      toast.success("Review submitted");
      setReviewFor(null);
      onRefresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <TabsContent value="performance">
      <div className="flex justify-end mb-4">
        <Button onClick={() => setGoalModal(true)} disabled={employees.length === 0} className="rounded-full bg-[#0B1B3D]" data-testid="assign-goal-btn">Assign Goal</Button>
      </div>
      {goals.length === 0 ? (
        <EmptyState title="No goals assigned" description="Assign goals and KPIs, then review employee self-assessments." testId="goals-empty" />
      ) : (
        <div className="space-y-3" data-testid="goals-list">
          {goals.map((g) => (
            <div key={g.id} className="bg-white border border-border/60 rounded-xl p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`goal-${g.id}`}>
              <div>
                <p className="font-semibold text-sm text-[#0B1B3D]">{g.title}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {g.employee_name} · KPI: {g.kpi || "—"} · Due {g.due_date || "flexible"}
                  {g.self_review && ` · Self: ${g.self_review.rating}/5`}
                  {g.manager_review && ` · Manager: ${g.manager_review.rating}/5 (${g.manager_review.recommendation})`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold rounded-full px-3 py-1 ${
                  g.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                  g.status === "self_reviewed" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"
                }`}>
                  {g.status === "completed" ? "Reviewed" : g.status === "self_reviewed" ? "Self-reviewed" : "Assigned"}
                </span>
                {g.status === "self_reviewed" && (
                  <Button size="sm" className="rounded-full bg-[#0B1B3D]" onClick={() => { setReviewFor(g); setReview({ rating: 5, feedback: "", recommendation: "none" }); }} data-testid={`review-goal-${g.id}`}>
                    Review
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={goalModal} onOpenChange={setGoalModal}>
        <DialogContent aria-describedby={undefined} data-testid="goal-dialog">
          <DialogHeader><DialogTitle>Assign goal</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Employee</Label>
              <Select value={goal.employee_id} onValueChange={(v) => setGoal({ ...goal, employee_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="goal-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name} — {e.designation}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Goal title</Label><Input value={goal.title} onChange={(e) => setGoal({ ...goal, title: e.target.value })} className="mt-1.5" data-testid="goal-title" /></div>
            <div><Label>KPI / target</Label><Input value={goal.kpi} onChange={(e) => setGoal({ ...goal, kpi: e.target.value })} className="mt-1.5" data-testid="goal-kpi" /></div>
            <div><Label>Due date</Label><Input type="date" value={goal.due_date} onChange={(e) => setGoal({ ...goal, due_date: e.target.value })} className="mt-1.5" data-testid="goal-due" /></div>
            <Button onClick={createGoal} disabled={!goal.employee_id || !goal.title} className="w-full rounded-full bg-[#0B1B3D]" data-testid="goal-submit-btn">Assign Goal</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reviewFor} onOpenChange={() => setReviewFor(null)}>
        <DialogContent aria-describedby={undefined} data-testid="review-dialog">
          <DialogHeader><DialogTitle>Review — {reviewFor?.title}</DialogTitle></DialogHeader>
          {reviewFor?.self_review && (
            <p className="text-xs bg-[#F8FAFC] border border-border rounded-lg p-3">
              Self-assessment: {reviewFor.self_review.rating}/5 — {reviewFor.self_review.notes || "no notes"}
            </p>
          )}
          <div className="space-y-4">
            <div>
              <Label>Rating (1–5)</Label>
              <Input type="number" min={1} max={5} value={review.rating} onChange={(e) => setReview({ ...review, rating: e.target.value })} className="mt-1.5" data-testid="review-rating" />
            </div>
            <div><Label>Feedback</Label><Textarea rows={3} value={review.feedback} onChange={(e) => setReview({ ...review, feedback: e.target.value })} className="mt-1.5" data-testid="review-feedback" /></div>
            <div>
              <Label>Recommendation</Label>
              <Select value={review.recommendation} onValueChange={(v) => setReview({ ...review, recommendation: v })}>
                <SelectTrigger className="mt-1.5" data-testid="review-recommendation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="increment">Increment</SelectItem>
                  <SelectItem value="promotion">Promotion</SelectItem>
                  <SelectItem value="pip">PIP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={submitReview} className="w-full rounded-full bg-[#0B1B3D]" data-testid="review-submit-btn">Submit Review</Button>
          </div>
        </DialogContent>
      </Dialog>
    </TabsContent>
  );
}
